import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

import { DocumentByIdDTO, UtilityBillByIdDTO } from "api";
import { MaybeNull } from "types";
import {
  Loading,
  FileViewerFooter,
  PdfNavigationProvider,
  PdfPreview,
} from "components";
import type { NavigationContext } from "components/file-viewer-footer";

import { AddDataEntryWrapper, UtilityBillForm } from "./components";

export const BillModal: React.FC<{
  document: DocumentByIdDTO | UtilityBillByIdDTO;
  isLoading: boolean;
  onClose: () => void;
  billId?: MaybeNull<string>;
  navigationContext?: NavigationContext;
  activeTab?: "document" | "entry";
  onNavigate?: (id: string) => void;
}> = ({
  document,
  isLoading,
  onClose,
  billId = null,
  navigationContext,
  activeTab,
  onNavigate,
}) => {
  const [scale, setScale] = useState<number>(0.75);

  // BE page numbers are 0-based; react-pdf expects 1-based.
  const toDisplayPage = (bePageNumber: MaybeNull<number> | undefined) =>
    (bePageNumber ?? 0) + 1;

  const [pageNumber, setPageNumber] = useState(() =>
    toDisplayPage((document as UtilityBillByIdDTO)?.pageNumber),
  );
  const [scrollVersion, setScrollVersion] = useState(0);
  const [activeBboxKey, setActiveBboxKey] = useState<string | undefined>();
  const activeBboxTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handlePageChange = useCallback((n: number, bboxKey?: string) => {
    setPageNumber(n);
    setScrollVersion((v) => v + 1);
    if (bboxKey) {
      setActiveBboxKey(bboxKey);
      clearTimeout(activeBboxTimerRef.current);
      activeBboxTimerRef.current = setTimeout(
        () => setActiveBboxKey(undefined),
        1800,
      );
    }
  }, []);

  useEffect(() => () => clearTimeout(activeBboxTimerRef.current), []);

  const handleLoadSuccess = useCallback(
    () =>
      setPageNumber(
        toDisplayPage((document as UtilityBillByIdDTO)?.pageNumber),
      ),
    [document],
  );

  const { downloadUrl, filename } = document as DocumentByIdDTO;

  useEffect(() => {
    if (scale <= 0.25) setScale(0.75);
  }, [scale]);

  if (!document) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PdfNavigationProvider navigateToPage={handlePageChange}>
        <Box
          sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}
        >
          <PdfPreview
            downloadUrl={downloadUrl}
            scale={scale}
            billId={billId}
            pageNumber={pageNumber}
            scrollVersion={scrollVersion}
            activeBboxKey={activeBboxKey}
            onLoadSuccess={handleLoadSuccess}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
              padding: 3,
              overflow: "auto",
            }}
          >
            {billId ? (
              <UtilityBillForm
                utilityBill={document as UtilityBillByIdDTO}
                onClose={onClose}
                billId={billId}
              />
            ) : (
              <AddDataEntryWrapper
                document={document as DocumentByIdDTO}
                onClose={onClose}
              />
            )}
          </Box>
        </Box>
      </PdfNavigationProvider>
      <FileViewerFooter
        fileName={filename}
        setScale={setScale}
        downloadUrl={downloadUrl as string}
        actionsDisabled={isLoading}
        showNavigationControls
        navigationContext={navigationContext}
        activeTab={activeTab}
        onNavigate={onNavigate}
      />
    </Box>
  );
};
